import { useEffect, useState } from "react";
import { useLanguage } from "@/lib/LanguageContext";
import { Wrench } from "lucide-react";

const navItems = [
  { id: "services", key: "services" },
  { id: "process", key: "process" },
  { id: "portfolio", key: "portfolio" },
  { id: "faq", key: "faq" },
  { id: "contact", key: "contact" },
];

export default function Header() {
  const { lang, toggle, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0B0C10]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="px-6 sm:px-12 lg:px-24 h-20 flex items-center justify-between">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 grid place-items-center bg-[#D97736] text-[#0B0C10] group-hover:bg-[#C2662B] transition-colors">
            <Wrench className="w-4 h-4" strokeWidth={2.5} />
          </span>
          <span className="font-display font-black text-lg tracking-tight">
            Schnegg.<span className="text-[#D97736]">.</span> und weg!
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="text-sm font-medium text-[#A0AAB2] hover:text-white transition-colors tracking-wide"
            >
              {t.nav[item.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="lang-toggle"
            className="hidden sm:flex items-center text-xs font-bold tracking-widest border border-white/15"
          >
            <button
              data-testid="lang-de"
              onClick={() => lang !== "de" && toggle()}
              className={`px-3 py-2 transition-colors ${
                lang === "de"
                  ? "bg-white text-[#0B0C10]"
                  : "text-[#A0AAB2] hover:text-white"
              }`}
            >
              DE
            </button>
            <button
              data-testid="lang-en"
              onClick={() => lang !== "en" && toggle()}
              className={`px-3 py-2 transition-colors ${
                lang === "en"
                  ? "bg-white text-[#0B0C10]"
                  : "text-[#A0AAB2] hover:text-white"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="header-cta"
            onClick={() => scrollTo("contact")}
            className="hidden md:inline-flex bg-[#D97736] hover:bg-[#C2662B] text-white px-5 py-3 font-bold tracking-wider uppercase text-xs transition-colors"
          >
            {t.nav.cta}
          </button>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setMobileOpen((s) => !s)}
            aria-label="Menu"
            className="lg:hidden w-10 h-10 grid place-items-center border border-white/15"
          >
            <span className="sr-only">Menu</span>
            <div className="flex flex-col gap-1.5">
              <span className="block w-5 h-px bg-white" />
              <span className="block w-5 h-px bg-white" />
              <span className="block w-5 h-px bg-white" />
            </div>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#0B0C10] border-t border-white/10 px-6 py-6 space-y-4"
        >
          {navItems.map((item) => (
            <button
              key={item.id}
              data-testid={`mobile-nav-${item.id}`}
              onClick={() => scrollTo(item.id)}
              className="block w-full text-left text-lg font-display font-bold text-white"
            >
              {t.nav[item.key]}
            </button>
          ))}
          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={() => lang !== "de" && toggle()}
              className={`px-3 py-2 text-xs font-bold tracking-widest border border-white/15 ${
                lang === "de" ? "bg-white text-[#0B0C10]" : "text-[#A0AAB2]"
              }`}
            >
              DE
            </button>
            <button
              onClick={() => lang !== "en" && toggle()}
              className={`px-3 py-2 text-xs font-bold tracking-widest border border-white/15 ${
                lang === "en" ? "bg-white text-[#0B0C10]" : "text-[#A0AAB2]"
              }`}
            >
              EN
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
